/**
 * A shipment has TWO dates and they are not interchangeable.
 *
 *   SERVICE  — the day the meal is eaten. Drives family/school ordering,
 *              production records (service logs), and cafeteria serving.
 *   DELIVERY — the day the truck arrives. Drives chef/kitchen order views
 *              and school-side RECEIVING temperature logs.
 *
 * On a same-day menu they are equal, which is why ~30 surfaces read
 * `serviceDate` and nobody noticed. Under a day-before delivery policy they
 * diverge and each of those reads is either right or wrong with no middle
 * ground. Routing every read through a NAMED axis makes the split greppable
 * instead of being thirty independent field accesses that all look correct.
 *
 * Pure and dependency-free: `packages/constants` is the only package already
 * depended on by `packages/schema`, `apps/ordo`, and `apps/menus`.
 */

export type ShipmentDateAxis = 'SERVICE' | 'DELIVERY'

export const SHIPMENT_DATE_AXES: readonly ShipmentDateAxis[] = ['SERVICE', 'DELIVERY']

/** The two dates every shipment carries. */
export interface ShipmentDates {
  serviceDate: Date | string
  deliveryDate: Date | string
}

/** Prisma field name for an axis — `serviceDate` or `deliveryDate`. */
export function shipmentDateField(axis: ShipmentDateAxis): 'serviceDate' | 'deliveryDate' {
  return axis === 'DELIVERY' ? 'deliveryDate' : 'serviceDate'
}

/** The date a shipment carries on the given axis. */
export function shipmentDateOn<T extends ShipmentDates>(
  shipment: T,
  axis: ShipmentDateAxis,
): (T[keyof T] & Date) | (T[keyof T] & string) {
  return (axis === 'DELIVERY' ? shipment.deliveryDate : shipment.serviceDate) as never
}

/**
 * `YYYY-MM-DD` bucket key for grouping shipments by day on one axis.
 *
 * Always UTC. Shipment dates are Prisma `@db.Date` — UTC-midnight instants —
 * so a local-time slice would bucket a shipment onto the wrong day for every
 * runner west of Greenwich. An already-`YYYY-MM-DD` string passes through.
 */
export function shipmentDayKey(shipment: ShipmentDates, axis: ShipmentDateAxis): string {
  const value = axis === 'DELIVERY' ? shipment.deliveryDate : shipment.serviceDate
  return typeof value === 'string' ? value.slice(0, 10) : value.toISOString().slice(0, 10)
}

/**
 * Spreadable Prisma `where` fragment for one axis:
 * `{ ...shipmentDateFilter('DELIVERY', day) }` → `{ deliveryDate: day }`.
 *
 * Takes any Prisma date filter, so ranges work too:
 * `shipmentDateFilter(axis, { gte: from, lte: to })`.
 */
export function shipmentDateFilter<V>(axis: ShipmentDateAxis, value: V): { serviceDate: V } | { deliveryDate: V } {
  return axis === 'DELIVERY' ? { deliveryDate: value } : { serviceDate: value }
}

/** Spreadable Prisma `orderBy` fragment for one axis. */
export function shipmentDateOrderBy(
  axis: ShipmentDateAxis,
  direction: 'asc' | 'desc' = 'asc',
): { serviceDate: 'asc' | 'desc' } | { deliveryDate: 'asc' | 'desc' } {
  return axis === 'DELIVERY' ? { deliveryDate: direction } : { serviceDate: direction }
}

/**
 * True when this shipment's two dates differ — i.e. it is delivered on a
 * different day than it is served.
 *
 * The UI contract: show the delivery date wherever a date appeared, and add
 * the service date ONLY when this returns true. A same-day kitchen then sees
 * exactly the chrome it sees today, and a split-day one gets the second date
 * precisely where it changes the answer.
 */
export function isSplitDayShipment(shipment: ShipmentDates): boolean {
  return shipmentDayKey(shipment, 'SERVICE') !== shipmentDayKey(shipment, 'DELIVERY')
}
